import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  Button,
  Skeleton,
} from "@mui/material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { AvailabilityService } from "./AvailabilityService";
import { ProviderAvailabilityScheduleItem } from "./ProviderAvailability";
import { BreadcrumbTexts } from "constant/BiPortalConstants";
import { useNavigate } from "react-router-dom";

interface NewRequestProps {
  onBreadcrumbChange: (text: string) => void;
  resetView?: boolean;
  providerCurrentShifts?: ProviderAvailabilityScheduleItem[];
  isLoading?: boolean;
  providerId: number | null;
  onShiftDataError?: (error: string) => void;
  providerInfo?: {
    providerId: number;
    providerEmail: string;
    providerName: string;
    providerClinicName: string;
  };
  onSuccess?: () => void;
}

const NewRequest: React.FC<NewRequestProps> = ({ 
  onBreadcrumbChange, 
  resetView, 
  providerCurrentShifts = [], 
  isLoading = false, 
  providerId,
  onShiftDataError,
  providerInfo,
  onSuccess,
}) => {
  const [selectedOption, setSelectedOption] = useState<string>("");
  const [showAvailabilityService, setShowAvailabilityService] = useState<boolean>(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (resetView) {
      setShowAvailabilityService(false);
      setSelectedOption("");
    }
  }, [resetView]);

  const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedOption(event.target.value);
  };

  const handleNext = () => {
    if (selectedOption === BreadcrumbTexts.setShift) {
      onBreadcrumbChange(BreadcrumbTexts.clientWaitingForService);
      setShowAvailabilityService(true);
    } else if (selectedOption === BreadcrumbTexts.additionalAvailability) {
      onBreadcrumbChange(BreadcrumbTexts.clientWaitingForService);
      setShowAvailabilityService(true);
    }
  };

  if (showAvailabilityService) {
    const additionalAvailabilityData = providerCurrentShifts
      .filter(item => item.isAdditionalShift === 1)
      .map(item => ({
        id: item.shiftId,
        schedule: `${item.daysOfWeek} ${item.startTime} - ${item.endTime}`.trim(),
        days: item.availableDays,
        startTime: item.startTime,
        endTime: item.endTime,
      }));
    
    return (
      <AvailabilityService 
        requestType={selectedOption as "Set Shift" | "Additional Availability"}
        currentSchedule={providerCurrentShifts}
        initialAdditionalAvailability={additionalAvailabilityData}
        providerId={providerId}
        onShiftDataError={onShiftDataError}
        providerInfo={providerInfo}
        onSuccess={onSuccess}
        isLoadingSchedule={isLoading}
      />
    );
  }

  const handleCancel = () => {
    navigate("/providerappointments")
  };

  return (
    <Box>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 400,
          mb: { xs: 2, md: 3 },
          fontSize: { xs: "20px", sm: "24px", md: "28px" },
        }}
      >
        Update or Add Availability
      </Typography>

      <Box sx={{ mb: { xs: 3, md: 4 } }}>
        <Typography
          variant="body1"
          sx={{
            mb: 2,
            color: "#000000",
            fontSize: { xs: "14px", sm: "16px" },
          }}
        >
          Your current schedule is shown below:
        </Typography>
        {isLoading ? (
          <Box component="ul" sx={{ pl: { xs: 2, sm: 3 }, mb: 0, mt: 0 }}>
            <Box component="li" sx={{ mb: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#000000",
                    fontSize: { xs: "13px", sm: "14px" },
                  }}
                >
                  Set Shift:
                </Typography>
                <Skeleton variant="text" width="60%" height={20} />
              </Box>
            </Box>
            <Box component="li" sx={{ mb: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#000000",
                    fontSize: { xs: "13px", sm: "14px" },
                  }}
                >
                  Additional Availability:
                </Typography>
                <Skeleton variant="text" width="50%" height={20} />
              </Box>
            </Box>
          </Box>
        ) : providerCurrentShifts.length > 0 ? (
          <Box component="ul" sx={{ pl: { xs: 2, sm: 3 }, mb: 0, mt: 0 }}>
            {(() => {
              const setShifts = providerCurrentShifts.filter(item => item.isAdditionalShift === 0);
              const additionalShifts = providerCurrentShifts.filter(item => item.isAdditionalShift === 1);
              
              return (
                <>
                  {setShifts.length > 0 ? (
                    setShifts.map((item) => (
                      <Box component="li" key={item.shiftId} sx={{ mb: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#000000",
                            fontSize: { xs: "13px", sm: "14px" },
                            wordBreak: "break-word",
                          }}
                        >
                          Set Shift: {item.daysOfWeek} at {item.startTime} - {item.endTime}
                        </Typography>
                      </Box>
                    ))
                  ) : (
                    <Box component="li" sx={{ mb: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#000000",
                          fontSize: { xs: "13px", sm: "14px" },
                          wordBreak: "break-word",
                        }}
                      >
                        Set Shift: None
                      </Typography>
                    </Box>
                  )}
                  
                  {additionalShifts.length > 0 ? (
                    <Box component="li" sx={{ mb: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#000000",
                          fontSize: { xs: "13px", sm: "14px" },
                          wordBreak: "break-word",
                        }}
                      >
                        Additional Availability: {additionalShifts.map((item, index) => {
                          const scheduleText = `${item.daysOfWeek} at ${item.startTime} - ${item.endTime}`;
                          if (index === 0) return scheduleText;
                          if (index === additionalShifts.length - 1) return ` and ${scheduleText}`;
                          return `, ${scheduleText}`;
                        }).join('')}
                      </Typography>
                    </Box>
                  ) : (
                    <Box component="li" sx={{ mb: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#000000",
                          fontSize: { xs: "13px", sm: "14px" },
                          wordBreak: "break-word",
                        }}
                      >
                        Additional Availability: None
                      </Typography>
                    </Box>
                  )}
                </>
              );
            })()}
          </Box>
        ) : (
          <Box component="ul" sx={{ pl: { xs: 2, sm: 3 }, mb: 0, mt: 0 }}>
            <Box component="li" sx={{ mb: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  color: "#000000",
                  fontSize: { xs: "13px", sm: "14px" },
                  wordBreak: "break-word",
                }}
              >
                Set Shift: None
              </Typography>
            </Box>
            <Box component="li" sx={{ mb: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  color: "#000000",
                  fontSize: { xs: "13px", sm: "14px" },
                  wordBreak: "break-word",
                }}
              >
                Additional Availability: None
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      <Box sx={{ mb: { xs: 3, md: 4 } }}>
        <Typography
          variant="body1"
          sx={{
            mb: { xs: 2, md: 3 },
            color: "#424242",
            fontWeight: 500,
            fontSize: { xs: "14px", sm: "16px" },
          }}
        >
          Please select an option to proceed
        </Typography>

        <RadioGroup
          value={selectedOption}
          onChange={handleOptionChange}
          sx={{ gap: { xs: 2, md: 3 } }}
        >
          <FormControlLabel
            value={BreadcrumbTexts.setShift}
            control={
              <Radio
                sx={{
                  color: "#666666",
                  alignSelf: "flex-start",
                  padding: { xs: "6px", sm: "9px" },
                  "&.Mui-checked": {
                    color: "#10389A",
                  },
                  "&:hover": {
                    backgroundColor: "rgba(16, 56, 154, 0.04)",
                  },
                }}
              />
            }
            label={
              <Box>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 500,
                    mb: 0.5,
                    fontSize: { xs: "14px", sm: "16px" },
                  }}
                >
                  Request Set Shift Change
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#666",
                    maxWidth: "600px",
                    fontSize: { xs: "12px", sm: "14px" },
                  }}
                >
                  View the blocks where clients are awaiting services and
                  request a new set shift. Please note: All set shifts
                  require a 6-month commitment.
                </Typography>
              </Box>
            }
            sx={{ alignItems: "flex-start", mb: 2, ml: 0 }}
          />
          <FormControlLabel
            value={BreadcrumbTexts.additionalAvailability}
            control={
              <Radio
                sx={{
                  color: "#666666",
                  alignSelf: "flex-start",
                  padding: { xs: "6px", sm: "9px" },
                  "&.Mui-checked": {
                    color: "#10389A",
                  },
                  "&:hover": {
                    backgroundColor: "rgba(16, 56, 154, 0.04)",
                  },
                }}
              />
            }
            label={
              <Box>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 500,
                    mb: 0.5,
                    fontSize: { xs: "14px", sm: "16px" },
                  }}
                >
                  Request Additional Availability
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#666",
                    maxWidth: "600px",
                    fontSize: { xs: "12px", sm: "14px" },
                  }}
                >
                  View the blocks outside of your set shift where clients
                  are awaiting services and request additional hours. Please note: All new requests require 6-month commitment
                </Typography>
              </Box>
            }
            sx={{ alignItems: "flex-start", ml: 0 }}
          />
        </RadioGroup>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: { xs: "flex-end", sm: "flex-end" },
          mt: { xs: 3, md: 4 },
        }}
      >
        <Button
          variant="text"
          onClick={handleCancel}
          sx={{
            color: "#10389A",
            textTransform: "none",
            minWidth: { xs: "120px", sm: "auto" },
            padding: { xs: "10px 20px", sm: "8px 22px" },
            fontSize: { xs: "14px", sm: "15px" },
            "&:hover": {
              backgroundColor: "#FFFFFF",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleNext}
          disabled={!selectedOption}
          endIcon={<ArrowForwardIosIcon />}
          sx={{
            backgroundColor: "#10389A",
            color: "#FFFFFF",
            textTransform: "none",
            minWidth: { xs: "120px", sm: "auto" },
            padding: { xs: "10px 20px", sm: "8px 22px" },
            fontSize: { xs: "14px", sm: "15px" },
            "&:hover": {
              backgroundColor: "#0c2a75",
            },
            "&.Mui-disabled": {
              backgroundColor: "#0000001F",
              color: "#00000061",
            },
          }}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
};

export default NewRequest;

